import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Box, CircularProgress } from "@mui/material";
import SkillBrick from "../SkillBrick";
import PlaySkillFocus from '../PlaySkillFocus/PlaySkillFocus.jsx';
import ErrorMessage from "../../../components/ErrorMessage.js";
import { useData } from "../../../DataContext.js";


const PlaySkills = ({user}) => {

    const userId = user.userId;
    const [skillsList, setSkillsList] = useState([]);
    const [focusSkill, setFocusSkill] = useState(null);
    const [state, setState] = useState('view');
    const [isTokenReady, setIsTokenReady] = useState(false);
    const [isError, setIsError] = useState(false);

    const { triggerAchievements } = useData();

    useEffect(() => {
        const fetchJwtToken = async () => {
          try {
            const session = await fetchAuthSession();
            const token = session.tokens.idToken;
            axios.defaults.headers['Authorization'] = `Bearer ${token}`;
            setIsTokenReady(true); 
          } catch (error) {
            console.error('Error fetching JWT token:', error);
            setIsError(true);
          }
        };
    
        fetchJwtToken();
    }, []);
    
    useEffect(() => {
        if (isTokenReady) {
            axios.get(process.env.REACT_APP_API_HOST + `/api/play-skills/${userId}`)
            .then(response => {
                const data = response.data;
                if (data !== null) {
                    setSkillsList(data);
                }
            })
            .catch(error => {
                console.error('Error fetching user play skills:', error);
                setIsError(true);
            });
        }
    }, [userId, isTokenReady]); 

    const counts = useMemo(() => ({
        achieved:   skillsList.filter(s => s.proficiency === 2).length,
        inProgress: skillsList.filter(s => s.proficiency !== 2 && (s.proficiency === 1 || s.attempts > 0)).length,
        total:      skillsList.length,
    }), [skillsList]);


    const handleFocus = (playSkill) => {
        setFocusSkill(playSkill);
        setState('focus');
    };

    const handleUpdate = (attempts, proficiency) => {
        const newSkill = {
            ...focusSkill,
            attempts: attempts,
            proficiency: proficiency
        }
        axios.post(process.env.REACT_APP_API_HOST + '/api/play-skills', newSkill, {
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { new_achievements } = response.data;
            triggerAchievements(new_achievements);
            setSkillsList(skillsList.map(skill => {
                if (skill.skill_id === focusSkill.skill_id) {
                    return newSkill;
                }
                else {
                    return skill;
                }
            }));
            setState('view');
        })
        .catch(error => {
            console.error('Error posting user play skill:', error);
            setIsError(true);
        });  
    };

    const handleExit = () => {
        setState('view');
    };

    if (isError) return <ErrorMessage handleConfirm={() => {setIsError(false); setState('view')}} />;
    
    if (skillsList?.length === 0) {
        return (
            <Box
                sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '65dvh',  // Full viewport height to center the content
                }}
            >
            <CircularProgress />
                <h3 style={{marginTop: '10px' }}>Loading...</h3>
            </Box>
        );
    }

    return (
        <>
            <div className='entries-in-tabs' style={{maxHeight: 'calc(100dvh - 135px)'}}>
                <div style={{
                    display: 'flex', gap: 16, justifyContent: 'flex-start', alignItems: 'center',
                    padding: '10px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 4,
                }}>
                    {[
                        { label: 'Achieved',    value: counts.achieved,   color: '#4ade80' },
                        { label: 'In Progress', value: counts.inProgress, color: '#fbbf24' },
                        { label: 'Total',       value: counts.total,      color: '#60a5fa' },
                    ].map(({ label, value, color }) => (
                        <div key={label} style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>{label}</div>
                        </div>
                    ))}
                </div>
                {skillsList?.map(skill => (
                    <SkillBrick className='entry' key={skill.skill_id} playSkill={skill} handleFocus={handleFocus} />
                ))}
            </div>

            {state === 'focus' && 
                <PlaySkillFocus playSkill={focusSkill} handleUpdate={handleUpdate} handleExit={handleExit} />
            }
        </>
    )

};

export default PlaySkills;
